<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="lg"
  >
    <template #header>
      <h4>
        <span v-if="isReadOnly">{{ $t('components.userAssets.modal.viewTitle') }}</span>
        <span v-else>{{ isEditMode ? $t('components.userAssets.modal.editTitle') : $t('components.userAssets.modal.createTitle') }}</span>
      </h4>
    </template>

    <template #body>
      <div class="user-asset-form">
        <!-- Alert for errors -->
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- Basic Information -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-person me-2" />
              {{ $t('components.userAssets.modal.basicInfo') }}
            </h5>

            <div class="row">
              <!-- Email -->
              <div class="col-md-6 mb-3">
                <label
                  for="email"
                  class="form-label required"
                >
                  {{ $t('components.userAssets.modal.fields.email') }}
                </label>
                <input
                  id="email"
                  v-model="v$.form.email.$model"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.email.$error || emailExists }"
                  :placeholder="$t('components.userAssets.modal.placeholders.email')"
                  :disabled="isReadOnly || isEditMode"
                >
                <div
                  v-if="v$.form.email.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.email.required.$invalid">
                    {{ $t('components.userAssets.modal.validation.emailRequired') }}
                  </div>
                  <div v-if="v$.form.email.email.$invalid">
                    {{ $t('components.userAssets.modal.validation.emailInvalid') }}
                  </div>
                </div>
                <div
                  v-if="emailExists && !v$.form.email.$error"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.userAssets.modal.validation.emailExists') }}
                </div>
                <div
                  v-if="checkingEmail"
                  class="text-muted small mt-1"
                >
                  <span class="spinner-border spinner-border-sm me-1" />
                  {{ $t('components.userAssets.modal.validation.checkingEmail') }}
                </div>
              </div>

              <!-- Password -->
              <div class="col-md-6 mb-3">
                <label
                  for="password"
                  class="form-label"
                  :class="{ required: !isEditMode }"
                >
                  {{ $t('components.userAssets.modal.fields.password') }}
                </label>
                <input
                  id="password"
                  v-model="v$.form.password.$model"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.password.$error }"
                  :placeholder="$t('components.userAssets.modal.placeholders.password')"
                  :disabled="isReadOnly"
                  autocomplete="new-password"
                >
                <div
                  v-if="v$.form.password.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.password.required.$invalid">
                    {{ $t('components.userAssets.modal.validation.passwordRequired') }}
                  </div>
                  <div v-if="v$.form.password.minLength.$invalid">
                    {{ $t('components.userAssets.modal.validation.passwordMinLength', { min: 6 }) }}
                  </div>
                </div>
                <small
                  v-if="isEditMode"
                  class="text-muted"
                >
                  {{ $t('components.userAssets.modal.hints.passwordOptional') }}
                </small>
              </div>
            </div>

            <div class="row">
              <!-- First Name -->
              <div class="col-md-6 mb-3">
                <label
                  for="name"
                  class="form-label required"
                >
                  {{ $t('components.userAssets.modal.fields.name') }}
                </label>
                <input
                  id="name"
                  v-model="v$.form.name.$model"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.name.$error }"
                  :placeholder="$t('components.userAssets.modal.placeholders.name')"
                  :disabled="isReadOnly"
                >
                <div
                  v-if="v$.form.name.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.name.required.$invalid">
                    {{ $t('components.userAssets.modal.validation.nameRequired') }}
                  </div>
                  <div v-if="v$.form.name.minLength.$invalid">
                    {{ $t('components.userAssets.modal.validation.nameMinLength', { min: 2 }) }}
                  </div>
                </div>
              </div>

              <!-- Last Name -->
              <div class="col-md-6 mb-3">
                <label
                  for="surname"
                  class="form-label required"
                >
                  {{ $t('components.userAssets.modal.fields.surname') }}
                </label>
                <input
                  id="surname"
                  v-model="v$.form.surname.$model"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.surname.$error }"
                  :placeholder="$t('components.userAssets.modal.placeholders.surname')"
                  :disabled="isReadOnly"
                >
                <div
                  v-if="v$.form.surname.$error"
                  class="invalid-feedback"
                >
                  <div v-if="v$.form.surname.required.$invalid">
                    {{ $t('components.userAssets.modal.validation.surnameRequired') }}
                  </div>
                  <div v-if="v$.form.surname.minLength.$invalid">
                    {{ $t('components.userAssets.modal.validation.surnameMinLength', { min: 2 }) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Phone -->
              <div class="col-md-6 mb-3">
                <label
                  for="phone"
                  class="form-label"
                >
                  {{ $t('components.userAssets.modal.fields.phone') }}
                </label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  class="form-control"
                  :placeholder="$t('components.userAssets.modal.placeholders.phone')"
                  :disabled="isReadOnly"
                >
              </div>

              <!-- Active Status (hidden for ROLE_ASSET_OWNER editing self) -->
              <div
                v-if="!isAssetOwnerEditingSelf"
                class="col-md-6 mb-3"
              >
                <label class="form-label">{{ $t('components.userAssets.modal.fields.status') }}</label>
                <div class="form-check form-switch">
                  <input
                    id="isActive"
                    v-model="form.active"
                    type="checkbox"
                    class="form-check-input"
                    role="switch"
                    :disabled="isReadOnly"
                  >
                  <label
                    for="isActive"
                    class="form-check-label"
                  >
                    {{ form.active ? $t('components.userAssets.modal.active') : $t('components.userAssets.modal.inactive') }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Roles -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-shield-check me-2" />
              {{ $t('components.userAssets.modal.roles') }}
            </h5>

            <!-- For ROLE_ASSET_OWNER editing self: show disabled checkbox -->
            <div
              v-if="isAssetOwnerEditingSelf"
              class="mb-3"
            >
              <label class="form-label">{{ $t('components.userAssets.modal.fields.roleType') }}</label>
              <div class="form-check">
                <input
                  id="role-type-owner"
                  type="checkbox"
                  class="form-check-input"
                  checked
                  disabled
                >
                <label
                  for="role-type-owner"
                  class="form-check-label"
                >
                  {{ $t('components.userAssets.modal.roleTypes.owner') || 'Właściciel' }}
                </label>
              </div>
              <small class="text-muted d-block mt-2">{{ $t('components.userAssets.modal.hints.ownerRole') || 'Rola właściciela nie może być zmieniana' }}</small>
            </div>

            <!-- For others: show radio buttons -->
            <div
              v-else
              class="mb-3"
            >
              <label class="form-label required">{{ $t('components.userAssets.modal.fields.roleType') }}</label>
              <div class="form-check">
                <input
                  id="role-type-admin"
                  v-model="form.roleType"
                  type="radio"
                  class="form-check-input"
                  value="admin"
                  :disabled="isReadOnly"
                >
                <label
                  for="role-type-admin"
                  class="form-check-label"
                >
                  {{ $t('components.userAssets.modal.roleTypes.admin') }}
                </label>
              </div>
              <div class="form-check mt-2">
                <input
                  id="role-type-user"
                  v-model="form.roleType"
                  type="radio"
                  class="form-check-input"
                  value="user"
                  :disabled="isReadOnly"
                >
                <label
                  for="role-type-user"
                  class="form-check-label"
                >
                  {{ $t('components.userAssets.modal.roleTypes.user') }}
                </label>
              </div>
              <div
                v-if="v$.form.roleType.$error"
                class="text-danger small mt-1"
              >
                <div v-if="v$.form.roleType.required.$invalid">
                  {{ $t('components.userAssets.modal.validation.roleTypeRequired') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Asset Groups Assignment (hidden for ROLE_ASSET_OWNER editing self) -->
          <div
            v-if="!isAssetOwnerEditingSelf"
            class="form-section"
          >
            <h5 class="form-section-title">
              <i class="bi bi-diagram-2 me-2" />
              {{ $t('components.userAssets.modal.groupAssignment') }}
            </h5>

            <div class="mb-3">
              <label
                for="user-asset-groups"
                class="form-label required"
              >
                {{ $t('components.userAssets.modal.fields.assetGroups') }}
              </label>
              <HierarchicalSelect
                v-model="v$.form.groupIds.$model"
                id="user-asset-groups"
                :nodes="groupTreeOptions"
                :placeholder="$t('components.userAssets.modal.noGroupsSelected')"
                :search-placeholder="$t('components.userAssets.modal.searchGroups')"
                :clear-label="$t('components.userAssets.modal.clearGroups')"
                :no-results-label="$t('components.userAssets.modal.noResults')"
                :no-nodes-label="$t('components.userAssets.modal.noGroups')"
                :searchable="true"
                :allow-null="true"
                :multiple="false"
                :disabled="isReadOnly"
              />
              <div
                v-if="v$.form.groupIds.$error"
                class="text-danger small mt-1"
              >
                <div v-if="v$.form.groupIds.required.$invalid">
                  {{ $t('components.userAssets.modal.validation.groupsRequired') }}
                </div>
              </div>
              <small class="text-muted">{{ $t('components.userAssets.modal.hints.assetGroups') }}</small>
            </div>
          </div>

          <!-- Notification Channels -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-bell me-2" />
              {{ $t('components.userAssets.modal.notificationChannels') }}
            </h5>

            <div class="mb-3">
              <label class="form-label">{{ $t('components.userAssets.modal.fields.channels') }}</label>
              <div class="form-check">
                <input
                  id="channel-email"
                  v-model="form.notificationChannels.email"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly"
                >
                <label
                  for="channel-email"
                  class="form-check-label"
                >
                  <i class="bi bi-envelope me-2" />
                  {{ $t('components.userAssets.modal.channels.email') }}
                </label>
              </div>
              <div class="form-check mt-2">
                <input
                  id="channel-sms"
                  v-model="form.notificationChannels.sms"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly"
                >
                <label
                  for="channel-sms"
                  class="form-check-label"
                >
                  <i class="bi bi-phone me-2" />
                  {{ $t('components.userAssets.modal.channels.sms') }}
                </label>
              </div>
              <div class="form-check mt-2">
                <input
                  id="channel-mercure"
                  v-model="form.notificationChannels.mercure"
                  type="checkbox"
                  class="form-check-input"
                  :disabled="isReadOnly"
                >
                <label
                  for="channel-mercure"
                  class="form-check-label"
                >
                  <i class="bi bi-broadcast me-2" />
                  {{ $t('components.userAssets.modal.channels.mercure') }}
                </label>
              </div>
              <small class="text-muted d-block mt-2">{{ $t('components.userAssets.modal.hints.notificationChannels') }}</small>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
        >
          {{ isReadOnly ? $t('components.userAssets.modal.close') : $t('components.userAssets.modal.cancel') }}
        </button>
        <button
          v-if="!isReadOnly"
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="handleSubmit"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
          />
          {{ isEditMode ? $t('components.userAssets.modal.update') : $t('components.userAssets.modal.create') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, requiredIf } from '@vuelidate/validators'
import BaseModal from './core/BaseModal.vue'
import HierarchicalSelect from '@/components/common/HierarchicalSelect.vue'
import { createUserAsset, updateUserAsset, getUserAsset, checkUserAssetEmail } from '@/crud/user-asset'
import { getAssetGroupsTree } from '@/crud/asset-group'
import { axiosInstance } from '@/http-common'
import { getUserIdFromJwt } from '@/crud/getters'
import { jwtDecode } from 'jwt-decode'

interface TreeNode {
  id: number
  label: string
  depth: number
  children?: TreeNode[]
  disabled?: boolean
}

export default defineComponent({
  name: 'UserAssetFormModal',
  components: {
    BaseModal,
    HierarchicalSelect
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const modalRef = ref()
    const loading = ref(false)
    const errorMessage = ref<string | null>(null)
    const isEditMode = ref(false)
    const isReadOnly = ref(false)
    const editingId = ref<number | null>(null)
    const emailExists = ref(false)
    const checkingEmail = ref(false)

    const groupTreeNodes = ref<TreeNode[]>([])

    const form = ref({
      email: '',
      password: '',
      name: '',
      surname: '',
      phone: null as string | null,
      roleType: '' as 'admin' | 'user' | '',
      active: true,
      groupIds: null as number | string | null, // Changed to single selection
      notificationChannels: {
        email: false,
        sms: false,
        mercure: false
      }
    })

    const rules = computed(() => ({
      form: {
        email: {
          required,
          email
        },
        password: {
          required: requiredIf(() => !isEditMode.value),
          minLength: minLength(6)
        },
        name: {
          required,
          minLength: minLength(2)
        },
        surname: {
          required,
          minLength: minLength(2)
        },
        roleType: {
          required: requiredIf(() => !isAssetOwnerEditingSelf.value)
        },
        groupIds: {
          required: (value: number | string | null) => {
            // Not required for ROLE_ASSET_OWNER editing self
            if (isAssetOwnerEditingSelf.value) return true
            // Required for others
            return value !== null
          }
        }
      }
    }))

    const v$ = useVuelidate(rules, { form })

    const groupTreeOptions = computed(() => {
      return groupTreeNodes.value
    })

    // Helper function to get current user's roles from JWT
    const getCurrentUserRoles = (): string[] => {
      try {
        const token = localStorage.getItem('jwt_token')
        if (!token) return []
        const decodedToken: any = jwtDecode(token)
        return decodedToken.roles || []
      } catch (e) {
        return []
      }
    }

    // Check if user is editing their own account
    const isEditingSelf = computed(() => {
      if (!isEditMode.value || !editingId.value) return false
      const currentUserId = getUserIdFromJwt()
      return currentUserId === editingId.value
    })

    // Check if ROLE_ASSET_OWNER is editing their own account
    const isAssetOwnerEditingSelf = computed(() => {
      if (!isEditingSelf.value) return false
      const roles = getCurrentUserRoles()
      return roles.includes('ROLE_ASSET_OWNER')
    })

    const resetForm = () => {
      form.value = {
        email: '',
        password: '',
        name: '',
        surname: '',
        phone: null,
        roleType: '',
        active: true,
        groupIds: null, // Changed to null for single selection
        notificationChannels: {
          email: false,
          sms: false,
          mercure: false
        }
      }
      errorMessage.value = null
      emailExists.value = false
      checkingEmail.value = false
      v$.value.$reset()
    }

    // Debounce timer for email check
    let emailCheckTimeout: ReturnType<typeof setTimeout> | null = null

    // Check if email exists (with debounce)
    const checkEmailAvailability = async (email: string) => {
      if (!email || email.length < 4 || isEditMode.value) {
        emailExists.value = false
        checkingEmail.value = false
        return
      }

      checkingEmail.value = true
      emailExists.value = false

      try {
        const response = await checkUserAssetEmail(email)
        emailExists.value = response.data?.exists || false
      } catch (error) {
        console.error('Error checking email:', error)
        emailExists.value = false
      } finally {
        checkingEmail.value = false
      }
    }

    watch(
      () => form.value.email,
      (newEmail) => {
        if (emailCheckTimeout) {
          clearTimeout(emailCheckTimeout)
        }

        emailExists.value = false

        if (newEmail && newEmail.length >= 4 && !isEditMode.value) {
          checkingEmail.value = true
          emailCheckTimeout = setTimeout(() => {
            checkEmailAvailability(newEmail)
          }, 500)
        } else {
          checkingEmail.value = false
        }
      }
    )

    const getRoleTypeFromRoles = (roles: string[]): 'admin' | 'user' => {
      if (roles.includes('ROLE_ASSET_ADMIN')) {
        return 'admin'
      }
      return 'user'
    }

    const getRolesFromRoleType = (roleType: 'admin' | 'user'): string[] => {
      if (roleType === 'admin') {
        return ['ROLE_ASSET_ADMIN', 'ROLE_ASSET']
      }
      return ['ROLE_USER', 'ROLE_ASSET']
    }

    const fetchGroupOptions = async () => {
      try {
        const response = await getAssetGroupsTree()
        const tree = response.data.tree || []

        const convertToTreeNodes = (nodes: any[], depth = 0): TreeNode[] => {
          return nodes.map(node => ({
            id: node.id,
            label: node.name,
            depth,
            children: node.children && node.children.length
              ? convertToTreeNodes(node.children, depth + 1)
              : undefined,
            disabled: false
          }))
        }

        groupTreeNodes.value = convertToTreeNodes(tree)
      } catch (e) {
        console.error('Failed to fetch group options:', e)
      }
    }

    const openModal = async (user: any = null, readonly = false) => {
      resetForm()
      isReadOnly.value = readonly

      await fetchGroupOptions()

      if (user) {
        isEditMode.value = true
        editingId.value = user.id

        // Fetch full user details including group assignments
        try {
          const response = await getUserAsset(user.id)
          const userData = response.data.user
          const groupIds = response.data.groupIds || []
          const notificationChannels = response.data.notificationChannels || []

          form.value.email = userData.email || ''
          form.value.password = '' // Password is never filled for edit
          form.value.name = userData.name || ''
          form.value.surname = userData.surname || ''
          form.value.phone = userData.phone || null
          form.value.roleType = Array.isArray(userData.roles) ? getRoleTypeFromRoles(userData.roles) : 'user'
          form.value.active = userData.active ?? true
          form.value.groupIds = groupIds.length > 0 ? groupIds[0] : null // Take first group for single selection

          // Load notification channels
          form.value.notificationChannels = {
            email: notificationChannels.includes('email'),
            sms: notificationChannels.includes('sms'),
            mercure: notificationChannels.includes('mercure')
          }
        } catch (e) {
          console.error('Failed to fetch user details:', e)
          // Fallback to basic data from grid
          form.value.email = user.email || ''
          form.value.password = ''
          form.value.name = user.name || ''
          form.value.surname = user.surname || ''
          form.value.phone = user.phone || null
          form.value.roleType = Array.isArray(user.roles) ? getRoleTypeFromRoles(user.roles) : 'user'
          form.value.active = user.active ?? true
          form.value.groupIds = null
          form.value.notificationChannels = {
            email: false,
            sms: false,
            mercure: false
          }
        }
      } else {
        isEditMode.value = false
        editingId.value = null
        form.value.roleType = 'user' // Default role type
      }

      await nextTick()
      modalRef.value.openModal()
    }

    const closeModal = () => {
      modalRef.value.closeModal()
      resetForm()
    }

    const handleSubmit = async () => {
      v$.value.$touch()
      if (v$.value.$invalid) {
        return
      }

      // Check if email already exists before submitting
      if (emailExists.value && !isEditMode.value) {
        errorMessage.value = t('components.userAssets.modal.validation.emailExists')
        return
      }

      loading.value = true
      errorMessage.value = null

      try {
        const payload: any = {
          email: form.value.email,
          name: form.value.name,
          surname: form.value.surname,
          phone: form.value.phone
        }

        // Only include password if it's provided
        if (form.value.password) {
          payload.password = form.value.password
        }

        // Convert notification channels checkboxes to array (always included)
        const notificationChannels: string[] = []
        if (form.value.notificationChannels.email) notificationChannels.push('email')
        if (form.value.notificationChannels.sms) notificationChannels.push('sms')
        if (form.value.notificationChannels.mercure) notificationChannels.push('mercure')
        payload.notificationChannels = notificationChannels

        // For ROLE_ASSET_OWNER editing self, only send basic fields + notificationChannels
        // For others, send all fields
        if (!isAssetOwnerEditingSelf.value) {
          // Convert roleType to roles array
          const roles = getRolesFromRoleType(form.value.roleType as 'admin' | 'user')

          payload.roles = roles
          payload.active = form.value.active
          payload.groupIds = form.value.groupIds !== null ? [form.value.groupIds] : []
        }

        let response
        if (isEditMode.value && editingId.value) {
          // Update existing user
          response = await updateUserAsset(editingId.value, payload)
          emit('saved', response.data)
          closeModal()
        } else {
          // Create new user
          response = await createUserAsset(payload)

          // After creating user in database, register in authorization service
          if (form.value.email) {
            try {
              const authServiceUrl = process.env.VUE_APP_AUTHORIZATION_SERVICE_URL
              await axiosInstance.post(`${authServiceUrl}/users/new`, {
                email: form.value.email
              })
              emit('saved', response.data)
              closeModal()
            } catch (authError: any) {
              errorMessage.value = t('components.userAssets.modal.authServiceError')
              console.error('Authorization service error:', authError)
              // User was created in DB but failed in auth service
              // Still emit saved to refresh grid
              emit('saved', response.data)
            }
          } else {
            emit('saved', response.data)
            closeModal()
          }
        }
      } catch (e: any) {
        errorMessage.value = e?.response?.data?.error || e?.message || t('components.userAssets.modal.error')
      } finally {
        loading.value = false
      }
    }

    return {
      modalRef,
      loading,
      errorMessage,
      isEditMode,
      isReadOnly,
      emailExists,
      checkingEmail,
      form,
      v$,
      groupTreeOptions,
      isAssetOwnerEditingSelf,
      openModal,
      closeModal,
      handleSubmit
    }
  }
})
</script>

<style scoped lang="scss">
.user-asset-form {
  .form-section {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid #e9ecef;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  .form-section-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #495057;

    i {
      color: #007bff;
    }
  }

  .required::after {
    content: " *";
    color: #dc3545;
  }
}
</style>
